import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Controller, useForm, useWatch } from 'react-hook-form'; // Import useWatch
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { ru } from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';
import { useSettings } from '../../contexts/SettingsContext'; // Import useSettings

// --- Interfaces ---
interface Client {
  id: number;
  name: string;
  // Add fields needed for requisites if available directly from option/search
  address?: string;
  bin?: string;
  taxId?: string;
  iik?: string;
  bankName?: string;
  bik?: string;
  phone?: string;
  email?: string;
  director?: string;
}

interface Order {
  id: number;
  orderNumber: string;
  startDate: string;
  endDate: string;
}

// Interface for Requisites (can be used for both company and client)
interface Requisites {
    name?: string;
    address?: string;
    bin?: string; // Or taxId
    iik?: string; // Or bankAccount
    bankName?: string;
    bik?: string; // Or bankBik
    phone?: string;
    email?: string;
    basisOfAuthority?: string; // Specific to company?
    director?: string; // Specific to client?
    // Add other fields as needed (KBE, OKPO etc.)
}

interface ContractFormData {
  id?: number;
  clientId: number | null;
  clientOption: { label: string; value: number; clientData?: Client } | null; // Include clientData
  orderId: number | null;
  orderOption: { label: string; value: number } | null;
  contractNumber: string;
  type: string;
  title?: string;
  issueDate: Date | null;
  startDate: Date | null;
  endDate: Date | null;
  signDate: Date | null;
  paymentTerms: string;
  notes?: string;
  status: string;
  content?: string; // Add content field
  termsAndConditions?: string; // Add terms field
  // Add requisites fields
  companyRequisites: Requisites;
  clientRequisites: Requisites;
}

interface SelectOption {
    label: string;
    value: number;
}

// Update ClientOption to potentially include full client data
interface ClientOption {
    label: string;
    value: number;
    clientData?: Client; // Make clientData optional but potentially available
}

const CONTRACT_STATUSES = [
  { value: 'draft', label: 'Черновик' },
  { value: 'sent', label: 'Отправлен' },
  { value: 'signed', label: 'Подписан' },
  // { value: 'active', label: 'Активный' }, // Remove if not in model ENUM
  // { value: 'completed', label: 'Завершен' }, // Remove if not in model ENUM
  { value: 'expired', label: 'Истек срок' }, // Add from model ENUM
  { value: 'cancelled', label: 'Отменен' } // Add from model ENUM
];

const ContractForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const { settings: companySettings, loading: loadingSettings } = useSettings(); // Get company settings

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientOrders, setClientOrders] = useState<Order[]>([]);
  const [loadingClientOrders, setLoadingClientOrders] = useState(false);
  const [selectedClientOption, setSelectedClientOption] = useState<ClientOption | null>(null);
  const [fetchedClientData, setFetchedClientData] = useState<Client | null>(null); // Store full client data

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm<ContractFormData>({
    defaultValues: {
      clientId: null,
      clientOption: null,
      orderId: null,
      orderOption: null,
      contractNumber: '',
      type: 'mainContract',
      title: '',
      issueDate: new Date(),
      startDate: null,
      endDate: null,
      signDate: null,
      paymentTerms: 'Предоплата 100%',
      notes: '',
      status: 'draft',
      content: '',
      termsAndConditions: '',
      // Initialize requisites as empty objects
      companyRequisites: {},
      clientRequisites: {},
    }
  });

  // --- Populate Company Requisites from Settings ---
  // Use useCallback to prevent re-creation on every render
  const populateCompanyRequisites = useCallback((settings: typeof companySettings) => {
      if (settings) {
          setValue('companyRequisites', {
              name: settings.name || '', // Default to empty string
              address: settings.address || '',
              // Use the fields defined in SettingsContext's Settings interface
              bin: settings.taxId || '', // Use taxId from context
              iik: settings.bankAccount || '', // Use bankAccount from context
              bankName: settings.bankName || '',
              bik: settings.bankBik || '', // Use bankBik from context
              phone: settings.phone || '',
              email: settings.email || '',
              director: settings.director || '',
              basisOfAuthority: settings.basisOfAuthority || ''
          }, { shouldValidate: false, shouldDirty: false }); // Avoid unnecessary validation/dirtying
      }
  }, [setValue]); // Dependency is only setValue

  useEffect(() => {
      // Populate only when creating a new contract and settings are loaded
      if (!isEditing && !loadingSettings && companySettings) {
          populateCompanyRequisites(companySettings);
      }
  // Ensure dependencies are correct, including populateCompanyRequisites
  }, [companySettings, loadingSettings, isEditing, populateCompanyRequisites]);

  // --- Load Contract Data (Editing) ---
  useEffect(() => {
    const loadContractData = async () => {
      if (isEditing && id) {
        setLoading(true);
        try {
          const response = await axios.get(`/contracts/${id}`);
          const contract = response.data;

          let initialClientOption: ClientOption | null = null;
          if (contract.clientId && contract.Client) {
             // Use client data directly from contract response if available
             initialClientOption = {
                 label: contract.Client.name,
                 value: contract.clientId,
                 clientData: contract.Client // Store full client data
             };
             setSelectedClientOption(initialClientOption);
             setFetchedClientData(contract.Client); // Store fetched client data
          }

          const formData: Partial<ContractFormData> = {
             id: contract.id,
             clientId: contract.clientId,
             clientOption: initialClientOption,
             orderId: contract.orderId,
             orderOption: null, // Will be set later if order exists
             contractNumber: contract.contractNumber,
             type: contract.type,
             title: contract.title || '',
             content: contract.content || '', // Load content
             termsAndConditions: contract.termsAndConditions || '', // Load terms
             issueDate: contract.issueDate ? new Date(contract.issueDate) : null,
             startDate: contract.startDate ? new Date(contract.startDate) : null,
             endDate: contract.endDate ? new Date(contract.endDate) : null,
             signDate: contract.signDate ? new Date(contract.signDate) : null,
             paymentTerms: contract.paymentTerms || '',
             notes: contract.notes || '',
             status: contract.status,
             // Load saved requisites
             companyRequisites: contract.companyRequisites || {},
             clientRequisites: contract.clientRequisites || {},
          };

          reset(formData); // Reset form with loaded data

        } catch (err) {
          console.error('Error fetching contract:', err);
          setError('Ошибка загрузки данных договора.');
        } finally {
          setLoading(false);
        }
      } else {
        // Reset for new contract
        // Define default company requisites based on loaded settings
        const defaultCompanyReqs: Requisites = companySettings ? {
            name: companySettings.name || '',
            address: companySettings.address || '',
            bin: companySettings.taxId || '', // Use correct field name from context
            iik: companySettings.bankAccount || '', // Use correct field name from context
            bankName: companySettings.bankName || '',
            bik: companySettings.bankBik || '', // Use correct field name from context
            phone: companySettings.phone || '',
            email: companySettings.email || '',
            director: companySettings.director || '',
            basisOfAuthority: companySettings.basisOfAuthority || ''
        } : {};

        // Call reset with the complete default state
        reset({
            clientId: null,
            clientOption: null,
            orderId: null,
            orderOption: null,
            contractNumber: '',
            type: 'mainContract',
            title: '',
            content: '',
            termsAndConditions: '',
            issueDate: new Date(),
            startDate: null,
            endDate: null,
            signDate: null,
            paymentTerms: 'Предоплата 100%',
            notes: '',
            status: 'draft',
            companyRequisites: defaultCompanyReqs, // Use pre-filled object
            clientRequisites: {} // Start with empty client requisites
        });
        setSelectedClientOption(null);
        setFetchedClientData(null);
        setLoading(false);
      }
    };
    // Run load only when company settings are potentially ready
    if (!loadingSettings) {
        loadContractData();
    }
  }, [id, isEditing, reset, loadingSettings, companySettings]); // Add loadingSettings and companySettings dependency

  // --- Fetch Client Orders and Populate Client Requisites ---
  useEffect(() => {
    const fetchClientDetailsAndOrders = async (clientId: number) => {
        setLoadingClientOrders(true);
        setValue('orderId', null);
        setValue('orderOption', null);
        setClientOrders([]);
        setFetchedClientData(null); // Reset fetched client data

        try {
            // Fetch full client details first
            let clientDetails: Client | null = null;
            try {
                const clientRes = await axios.get(`/clients/${clientId}`);
                clientDetails = clientRes.data;
                setFetchedClientData(clientDetails); // Store full client data

                // Populate client requisites form fields
                setValue('clientRequisites', {
                    name: clientDetails?.name || '',
                    address: clientDetails?.address || '',
                    bin: clientDetails?.bin || clientDetails?.taxId || '',
                    iik: clientDetails?.iik || '',
                    bankName: clientDetails?.bankName || '',
                    bik: clientDetails?.bik || '',
                    phone: clientDetails?.phone || '',
                    email: clientDetails?.email || '',
                    director: clientDetails?.director
                });

            } catch (clientErr) {
                 console.error('Error fetching full client details:', clientErr);
                 // Still try to populate with what we have from the option if available
                 const fallbackClientData = selectedClientOption?.clientData;
                 setValue('clientRequisites', {
                    name: fallbackClientData?.name || '',
                    address: fallbackClientData?.address || '',
                    bin: fallbackClientData?.bin || fallbackClientData?.taxId || '',
                    iik: fallbackClientData?.iik || '',
                    bankName: fallbackClientData?.bankName || '',
                    bik: fallbackClientData?.bik || '',
                    phone: fallbackClientData?.phone || '',
                    email: fallbackClientData?.email || '',
                    director: fallbackClientData?.director
                 });
            }


            // Fetch client orders
            interface OrdersApiResponse { orders: Order[]; }
            const response = await axios.get<OrdersApiResponse>('/orders', {
                params: { clientId: clientId, limit: 1000 }
            });
            const fetchedOrders = response.data?.orders || [];
            setClientOrders(fetchedOrders);

            // Set initial order option if editing and orderId exists
            if (isEditing) {
                const initialOrderId = control._formValues.orderId; // Get orderId from form state
                const initialOrder = fetchedOrders.find(o => o.id === initialOrderId);
                if (initialOrder) {
                   setValue('orderOption', {
                       label: `Заказ №${initialOrder.orderNumber} (${formatDate(initialOrder.startDate)} - ${formatDate(initialOrder.endDate)})`,
                       value: initialOrder.id
                    });
                }
            }

        } catch (error) {
            console.error('Error fetching client details or orders:', error);
            setError('Ошибка загрузки данных клиента или заказов.');
        } finally {
            setLoadingClientOrders(false);
        }
    };

    if (selectedClientOption?.value) {
        setValue('clientId', selectedClientOption.value);
        fetchClientDetailsAndOrders(selectedClientOption.value);
    } else {
        setValue('clientId', null);
        setClientOrders([]);
        setValue('orderId', null);
        setValue('orderOption', null);
        // Clear client requisites if client is deselected
        setValue('clientRequisites', {});
        setFetchedClientData(null);
    }
  }, [selectedClientOption, setValue, isEditing, control]); // Removed reset dependency

  // --- Helper Functions ---
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
         return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
    } catch (e) {
        return '-';
    }
  };

  const loadClientOptions = async (inputValue: string): Promise<ClientOption[]> => {
    try {
      // Modify endpoint if it can return more client data for requisites
      const response = await axios.get(`/clients/options?search=${inputValue}`);
      // Ensure response.data is an array
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error loading client options:', error);
      return [];
    }
  };

  const orderOptions = useMemo((): SelectOption[] => {
    if (!Array.isArray(clientOrders)) return [];
    return clientOrders.map(order => ({
      label: `Заказ №${order.orderNumber} (${formatDate(order.startDate)} - ${formatDate(order.endDate)})`,
      value: order.id
    }));
  }, [clientOrders]);

  const handleOrderSelectChange = (option: SelectOption | null) => {
        setValue('orderOption', option);
        setValue('orderId', option?.value ?? null);

        const selectedOrder = clientOrders.find(order => order.id === option?.value);
        if (selectedOrder) {
            // Optionally update contract dates based on order dates
            // setValue('startDate', selectedOrder.startDate ? new Date(selectedOrder.startDate) : null, { shouldValidate: true });
            // setValue('endDate', selectedOrder.endDate ? new Date(selectedOrder.endDate) : null, { shouldValidate: true });
        } else {
             // Clear dates if order is deselected? Decide on behavior.
             // setValue('startDate', null);
             // setValue('endDate', null);
        }
    };

  // --- Form Submission ---
  const onSubmit = async (data: ContractFormData) => {
    setSubmitting(true);
    setError(null);

    // Ensure requisites are objects, even if empty
    const companyReqs = data.companyRequisites || {};
    const clientReqs = data.clientRequisites || {};

    const payload = {
       ...data,
       clientOption: undefined, // Remove select option object
       orderOption: undefined, // Remove select option object
       issueDate: data.issueDate ? data.issueDate.toISOString().split('T')[0] : null,
       startDate: data.startDate ? data.startDate.toISOString().split('T')[0] : null,
       endDate: data.endDate ? data.endDate.toISOString().split('T')[0] : null,
       signDate: data.signDate ? data.signDate.toISOString().split('T')[0] : null,
       // Ensure requisites are included
       companyRequisites: companyReqs,
       clientRequisites: clientReqs,
    };

    // Remove id for creation
    if (!isEditing) {
        delete payload.id;
    }

    console.log("Submitting Payload:", payload); // Debugging

    try {
      if (isEditing && id) {
        await axios.put(`/contracts/${id}`, payload);
      } else {
        await axios.post('/contracts', payload);
      }
      navigate('/contracts');
    } catch (err: any) {
      console.error('Error saving contract:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Ошибка при сохранении договора.';
      setError(errorMsg);
      // Log the detailed error response if available
      if (err.response?.data) {
          console.error("Server Error Details:", err.response.data);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Watch client selection to disable order dropdown
  const watchedClientId = useWatch({ control, name: 'clientId' });

  if (loading || loadingSettings) { // Show loading if either contract or settings are loading
    return <LoadingSpinner />;
  }

  // --- Render Function ---
  return (
    <div>
      <PageHeader
        title={isEditing ? 'Редактирование договора' : 'Новый договор'}
        subtitle={isEditing ? `Договор № ${watch('contractNumber') || id}` : 'Создание нового договора'}
        backLink="/contracts"
      />

       {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow p-6 space-y-8">
        {/* --- Основная информация --- */}
        <section>
             <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4 border-b pb-2">Основная информация</h3>
             <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                    <label htmlFor="clientId" className="form-label">
                        Клиент <span className="text-red-500">*</span>
                    </label>
                     <Controller
                        name="clientId"
                        control={control}
                        rules={{ required: 'Необходимо выбрать клиента' }}
                        render={({ field, fieldState }) => (
                            <AsyncSelect<ClientOption>
                                inputId="clientId"
                                cacheOptions
                                defaultOptions
                                loadOptions={loadClientOptions}
                                value={selectedClientOption}
                                onChange={(option: ClientOption | null) => {
                                    field.onChange(option ? option.value : null);
                                    setSelectedClientOption(option);
                                }}
                                placeholder="Начните вводить имя клиента..."
                                classNamePrefix="react-select"
                                noOptionsMessage={({ inputValue }) => inputValue ? "Клиенты не найдены" : "Начните вводить..."}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderColor: fieldState.error ? '#ef4444' : base.borderColor
                                    }),
                                    menu: (base) => ({ ...base, zIndex: 30 }) // Ensure dropdown is on top
                                }}
                            />
                        )}
                     />
                    {errors.clientId && <p className="mt-1 text-sm text-red-600">{errors.clientId.message}</p>}
                </div>

                <div className="sm:col-span-3">
                     <label htmlFor="orderOption" className="form-label">
                        Связанный Заказ (необязательно)
                    </label>
                    <Controller
                        name="orderOption"
                        control={control}
                        render={({ field }) => (
                             <Select
                                 {...field}
                                 id="orderOption"
                                 instanceId="order-select"
                                 options={orderOptions}
                                 isLoading={loadingClientOrders}
                                 isDisabled={!watchedClientId || loadingClientOrders} // Use watchedClientId
                                 placeholder={watchedClientId ? 'Выберите заказ...' : 'Сначала выберите клиента'}
                                 noOptionsMessage={() => 'Нет доступных заказов для клиента'}
                                 isClearable
                                 onChange={handleOrderSelectChange}
                                 classNamePrefix="react-select"
                                 styles={{ menu: (base) => ({ ...base, zIndex: 29 }) }} // Adjust z-index if needed
                             />
                        )}
                     />
                    {errors.orderOption && <p className="mt-1 text-sm text-red-600">{errors.orderOption.message}</p>}
                 </div>

                 <div className="sm:col-span-3">
                    <label htmlFor="contractNumber" className="form-label">
                        Номер договора
                    </label>
                    <input
                        id="contractNumber"
                        type="text"
                        {...register('contractNumber')}
                        className="form-input bg-gray-100" // Make read-only visually distinct
                        readOnly
                        placeholder="(Генерируется автоматически)"
                    />
                </div>

                 <div className="sm:col-span-3">
                     <label htmlFor="status" className="form-label">
                         Статус <span className="text-red-500">*</span>
                     </label>
                     <Controller
                        name="status"
                        control={control}
                        rules={{ required: 'Статус обязателен' }}
                        render={({ field }) => (
                             <Select
                                 {...field}
                                 id="status"
                                 instanceId="status-select"
                                 options={CONTRACT_STATUSES}
                                 value={CONTRACT_STATUSES.find(c => c.value === field.value)}
                                 onChange={(option: any) => field.onChange(option?.value)}
                                 placeholder="Выберите статус..."
                                 classNamePrefix="react-select"
                                 styles={{ menu: (base) => ({ ...base, zIndex: 28 }) }} // Adjust z-index if needed
                             />
                        )}
                     />
                    {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>}
                 </div>

                 <div className="sm:col-span-6">
                    <label htmlFor="title" className="form-label">
                        Заголовок / Предмет договора <span className="text-red-500">*</span>
                    </label>
                    <input
                        id="title"
                        type="text"
                        {...register('title', { required: 'Заголовок договора обязателен' })}
                        className={`form-input ${errors.title ? 'border-red-500' : ''}`}
                        placeholder="Краткое описание предмета договора"
                    />
                     {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>}
                </div>

                 <div className="sm:col-span-3">
                    <label htmlFor="type" className="form-label">Тип</label>
                    <input
                        id="type"
                        type="text"
                        {...register('type')}
                        className={`form-input ${errors.type ? 'border-red-500' : ''}`}
                        placeholder="Например, mainContract, additionalAgreement"
                    />
                    {errors.type && <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>}
                </div>
            </div>
        </section>

        {/* --- Даты договора --- */}
        <section>
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4 border-b pb-2">Даты договора</h3>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                 <div className="sm:col-span-2">
                    <label htmlFor="issueDate" className="form-label">Дата составления *</label>
                    <Controller
                        name="issueDate"
                        control={control}
                        rules={{ required: 'Дата составления обязательна' }}
                        render={({ field }) => (
                            <DatePicker
                                selected={field.value}
                                onChange={(date: Date | null) => field.onChange(date)}
                                locale={ru}
                                dateFormat="dd.MM.yyyy"
                                className={`form-input w-full ${errors.issueDate ? 'border-red-500' : ''}`}
                                placeholderText="ДД.ММ.ГГГГ"
                                autoComplete="off"
                            />
                        )}
                    />
                    {errors.issueDate && <p className="mt-1 text-sm text-red-600">{errors.issueDate.message}</p>}
                 </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="startDate" className="form-label">Дата начала действия</label>
                     <Controller
                        name="startDate"
                        control={control}
                        // rules={{ required: 'Дата начала обязательна' }} // Make optional?
                        render={({ field }) => (
                            <DatePicker
                                selected={field.value}
                                onChange={(date: Date | null) => field.onChange(date)}
                                 selectsStart
                                 startDate={field.value}
                                 endDate={watch('endDate')}
                                locale={ru}
                                dateFormat="dd.MM.yyyy"
                                className={`form-input w-full ${errors.startDate ? 'border-red-500' : ''}`}
                                placeholderText="ДД.ММ.ГГГГ"
                                autoComplete="off"
                                isClearable // Allow clearing date
                            />
                        )}
                    />
                    {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate.message}</p>}
                 </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="endDate" className="form-label">Дата окончания действия</label>
                    <Controller
                        name="endDate"
                        control={control}
                        rules={{
                            // required: 'Дата окончания обязательна', // Make optional?
                            validate: (value) => {
                                const startDate = watch('startDate');
                                if (startDate instanceof Date && value instanceof Date) {
                                    return value >= startDate || 'Дата окончания не может быть раньше даты начала';
                                }
                                return true;
                            }
                         }}
                        render={({ field }) => {
                            const startDateValue = watch('startDate');
                            return (
                                <DatePicker
                                    selected={field.value}
                                    onChange={(date: Date | null) => field.onChange(date)}
                                    selectsEnd
                                    startDate={startDateValue}
                                    endDate={field.value}
                                    minDate={startDateValue instanceof Date ? new Date(startDateValue) : undefined}
                                    locale={ru}
                                    dateFormat="dd.MM.yyyy"
                                    className={`form-input w-full ${errors.endDate ? 'border-red-500' : ''}`}
                                    placeholderText="ДД.ММ.ГГГГ"
                                    autoComplete="off"
                                    isClearable // Allow clearing date
                                />
                            );
                        }}
                    />
                    {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate.message}</p>}
                 </div>

                 <div className="sm:col-span-2">
                    <label htmlFor="signDate" className="form-label">Дата подписания</label>
                     <Controller
                        name="signDate"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                selected={field.value}
                                onChange={(date: Date | null) => field.onChange(date)}
                                locale={ru}
                                dateFormat="dd.MM.yyyy"
                                className={`form-input w-full ${errors.signDate ? 'border-red-500' : ''}`}
                                placeholderText="ДД.ММ.ГГГГ"
                                isClearable
                                autoComplete="off"
                            />
                        )}
                    />
                     {errors.signDate && <p className="mt-1 text-sm text-red-600">{errors.signDate.message}</p>}
                 </div>
            </div>
        </section>

        {/* --- Реквизиты Компании --- */}
        <section>
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4 border-b pb-2">Реквизиты Исполнителя (Ваша Компания)</h3>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                {/* Use register with dot notation for nested objects */}
                <div className="sm:col-span-6">
                    <label htmlFor="companyRequisites.name" className="form-label">Название</label>
                    <input id="companyRequisites.name" {...register('companyRequisites.name')} className="form-input" />
                </div>
                 <div className="sm:col-span-6">
                    <label htmlFor="companyRequisites.address" className="form-label">Адрес</label>
                    <input id="companyRequisites.address" {...register('companyRequisites.address')} className="form-input" />
                </div>
                 <div className="sm:col-span-2">
                    <label htmlFor="companyRequisites.bin" className="form-label">БИН/ИИН</label>
                    <input id="companyRequisites.bin" {...register('companyRequisites.bin')} className="form-input" />
                </div>
                 <div className="sm:col-span-4">
                    <label htmlFor="companyRequisites.iik" className="form-label">ИИК</label>
                    <input id="companyRequisites.iik" {...register('companyRequisites.iik')} className="form-input" />
                </div>
                 <div className="sm:col-span-3">
                    <label htmlFor="companyRequisites.bankName" className="form-label">Банк</label>
                    <input id="companyRequisites.bankName" {...register('companyRequisites.bankName')} className="form-input" />
                </div>
                 <div className="sm:col-span-3">
                    <label htmlFor="companyRequisites.bik" className="form-label">БИК</label>
                    <input id="companyRequisites.bik" {...register('companyRequisites.bik')} className="form-input" />
                </div>
                 <div className="sm:col-span-3">
                    <label htmlFor="companyRequisites.phone" className="form-label">Телефон</label>
                    <input id="companyRequisites.phone" {...register('companyRequisites.phone')} className="form-input" />
                </div>
                 <div className="sm:col-span-3">
                    <label htmlFor="companyRequisites.email" className="form-label">Email</label>
                    <input id="companyRequisites.email" type="email" {...register('companyRequisites.email')} className="form-input" />
                </div>
                 <div className="sm:col-span-3">
                    <label htmlFor="companyRequisites.director" className="form-label">Директор</label>
                    <input id="companyRequisites.director" {...register('companyRequisites.director')} className="form-input" />
                </div>
                 <div className="sm:col-span-3">
                    <label htmlFor="companyRequisites.basisOfAuthority" className="form-label">Действует на основании</label>
                    <input id="companyRequisites.basisOfAuthority" {...register('companyRequisites.basisOfAuthority')} className="form-input" />
                </div>
            </div>
        </section>

        {/* --- Реквизиты Клиента --- */}
        <section>
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4 border-b pb-2">Реквизиты Заказчика (Клиент)</h3>
             <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-6">
                    <label htmlFor="clientRequisites.name" className="form-label">Название</label>
                    <input id="clientRequisites.name" {...register('clientRequisites.name')} className="form-input" />
                </div>
                 <div className="sm:col-span-6">
                    <label htmlFor="clientRequisites.address" className="form-label">Адрес</label>
                    <input id="clientRequisites.address" {...register('clientRequisites.address')} className="form-input" />
                </div>
                 <div className="sm:col-span-2">
                    <label htmlFor="clientRequisites.bin" className="form-label">БИН/ИИН</label>
                    <input id="clientRequisites.bin" {...register('clientRequisites.bin')} className="form-input" />
                </div>
                 <div className="sm:col-span-4">
                    <label htmlFor="clientRequisites.iik" className="form-label">ИИК</label>
                    <input id="clientRequisites.iik" {...register('clientRequisites.iik')} className="form-input" />
                </div>
                 <div className="sm:col-span-3">
                    <label htmlFor="clientRequisites.bankName" className="form-label">Банк</label>
                    <input id="clientRequisites.bankName" {...register('clientRequisites.bankName')} className="form-input" />
                </div>
                 <div className="sm:col-span-3">
                    <label htmlFor="clientRequisites.bik" className="form-label">БИК</label>
                    <input id="clientRequisites.bik" {...register('clientRequisites.bik')} className="form-input" />
                </div>
                 <div className="sm:col-span-3">
                    <label htmlFor="clientRequisites.phone" className="form-label">Телефон</label>
                    <input id="clientRequisites.phone" {...register('clientRequisites.phone')} className="form-input" />
                </div>
                 <div className="sm:col-span-3">
                    <label htmlFor="clientRequisites.email" className="form-label">Email</label>
                    <input id="clientRequisites.email" type="email" {...register('clientRequisites.email')} className="form-input" />
                </div>
                 <div className="sm:col-span-3">
                    <label htmlFor="clientRequisites.director" className="form-label">Директор</label>
                    <input id="clientRequisites.director" {...register('clientRequisites.director')} className="form-input" />
                </div>
                 {/* Add fields for KBE, OKPO etc. if needed */}
            </div>
        </section>

        {/* --- Условия и Примечания --- */}
        <section>
             <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4 border-b pb-2">Условия и Примечания</h3>
             <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                 <div className="sm:col-span-6">
                    <label htmlFor="paymentTerms" className="form-label">Условия оплаты *</label>
                    <input
                        id="paymentTerms"
                        type="text"
                        {...register('paymentTerms', { required: 'Условия оплаты обязательны' })}
                        className={`form-input ${errors.paymentTerms ? 'border-red-500' : ''}`}
                        placeholder="Например, Предоплата 100%"
                    />
                     {errors.paymentTerms && <p className="mt-1 text-sm text-red-600">{errors.paymentTerms.message}</p>}
                </div>

                 <div className="sm:col-span-6">
                    <label htmlFor="termsAndConditions" className="form-label">Особые условия / Положения</label>
                    <textarea
                        id="termsAndConditions"
                        rows={4}
                        {...register('termsAndConditions')}
                        className={`form-input ${errors.termsAndConditions ? 'border-red-500' : ''}`}
                        placeholder="Дополнительные условия договора"
                    />
                     {errors.termsAndConditions && <p className="mt-1 text-sm text-red-600">{errors.termsAndConditions.message}</p>}
                </div>

                 <div className="sm:col-span-6">
                    <label htmlFor="notes" className="form-label">Примечания</label>
                    <textarea
                        id="notes"
                        rows={4}
                        {...register('notes')}
                        className={`form-input ${errors.notes ? 'border-red-500' : ''}`}
                        placeholder="Внутренние заметки по договору"
                    />
                     {errors.notes && <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>}
                </div>

                 {/* Consider adding content field if it's editable */}
                 {/* <div className="sm:col-span-6">
                    <label htmlFor="content" className="form-label">Содержание договора (HTML)</label>
                    <textarea
                        id="content"
                        rows={10}
                        {...register('content')}
                        className={`form-input ${errors.content ? 'border-red-500' : ''}`}
                        placeholder="Полный текст договора в формате HTML"
                    />
                     {errors.content && <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>}
                </div> */}
            </div>
        </section>

        {/* --- Кнопки --- */}
        <div className="pt-8 flex justify-end space-x-3 border-t mt-8">
          <button
            type="button"
            onClick={() => navigate('/contracts')}
            className="btn-secondary"
            disabled={submitting}
          >
            Отмена
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={submitting || loading || loadingSettings} // Disable if settings are loading too
          >
            {submitting ? <LoadingSpinner /> : (isEditing ? 'Сохранить изменения' : 'Создать договор')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContractForm;
